import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { jwtDecode } from "jwt-decode";
// ArcGIS Core იმპორტები
import esriConfig from "@arcgis/core/config";
import * as urlUtils from "@arcgis/core/core/urlUtils";
import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import esriRequest from "@arcgis/core/request";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Compass from "@arcgis/core/widgets/Compass";
import Home from "@arcgis/core/widgets/Home";
import Locate from "@arcgis/core/widgets/Locate";
import BasemapGallery from "@arcgis/core/widgets/BasemapGallery";
import Measurement from "@arcgis/core/widgets/Measurement";
import Print from "@arcgis/core/widgets/Print";
import Expand from "@arcgis/core/widgets/Expand";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";

import Graphic from "@arcgis/core/Graphic";

import { createDynamicLayers, createSearchSources, configurePopups } from "./MapUtils";
import { setupBasemapGallery } from "./MapWidgets";

// Module-level helpers — no hook closure dependencies
const _getServiceName = (url) => {
    const parts = url.toLowerCase().split("/rest/services/")[1];
    if (!parts) return url.toLowerCase();
    const splitParts = parts.split("/");
    return splitParts[0] + "/" + splitParts[1];
};

const _findMapImageLayerByBaseUrl = (map, searchUrl) => {
    const searchServiceName = _getServiceName(searchUrl);
    return map.layers.find(l => l.url && _getServiceName(l.url) === searchServiceName);
};

const _stripDetailField = (tmpl) => {
    if (!tmpl) return;
    const HIDDEN_LABELS = new Set(["დეტალური", "ბმული"]);
    const keep = (fi) => {
        if (HIDDEN_LABELS.has(fi.label)) return false;
        const name = fi.fieldName?.toLowerCase() ?? "";
        if (name.includes("detail") || name.includes("bmuli") || name.includes("link")) return false;
        return true;
    };
    if (tmpl.fieldInfos?.length) tmpl.fieldInfos = tmpl.fieldInfos.filter(keep);
    if (Array.isArray(tmpl.content)) {
        tmpl.content.forEach(item => {
            if (item.fieldInfos?.length) item.fieldInfos = item.fieldInfos.filter(keep);
        });
    }
};

const _enableSublayerWithParents = (layer, sublayerId) => {
    if (!layer || layer.type !== "map-image") return;
    const id = parseInt(sublayerId, 10);
    const sublayer = layer.findSublayerById(id);
    if (sublayer) {
        sublayer.visible = true;
        let current = sublayer.parent;
        while (current) {
            current.visible = true;
            current = current.parent;
        }
        layer.visible = true;
    }
};

export const useMap = (mapDivRef) => {
    const [layersStructure, setLayersStructure] = useState([]);
    const [isLightLayer, setIsLightLayer] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const viewRef = useRef(null);
    const measurementRef = useRef(null);
    const homeRef = useRef(null);
    const locateRef = useRef(null);
    const searchSourcesRef = useRef([]);
    const infoModeRef = useRef(true);      // mirrors infoActive in InteractiveMap
    const programmaticRef = useRef(false); // true while navigateToSearchResult opens popup
    const cleanBaseURL = "https://tcsa.gov.ge";
    const { t, i18n } = useTranslation();
    const [role, setRole] = useState(null);

    useEffect(() => {
        const token = localStorage.getItem("jwtToken");
        if (token) {
            try {
                const decoded = jwtDecode(token);
                setRole(decoded.role);
            } catch (error) { console.error(error); }
        }
    }, []);

    const navigateToSearchResult = useCallback(async ({ feature, customData, layer: searchLayer }) => {
        if (!feature || !viewRef.current) return;
        const layerId = customData?.layerId;
        const baseUrl = customData?.baseUrl;
        const currentView = viewRef.current;
        const geom = feature.geometry;

        // ── Selection graphic (works on any layer type, unlike layerView.highlight) ──
        currentView.graphics.removeAll();
        if (geom) {
            let symbol;
            if (geom.type === "polygon") {
                symbol = { type: "simple-fill", color: [0, 121, 194, 0.15],
                    outline: { color: [0, 121, 194], width: 3 } };
            } else if (geom.type === "polyline") {
                symbol = { type: "simple-line", color: [0, 121, 194], width: 3 };
            } else {
                symbol = { type: "simple-marker", color: [0, 121, 194], size: 14,
                    outline: { color: [255, 255, 255], width: 2 } };
            }
            currentView.graphics.add(new Graphic({ geometry: geom, symbol }));
        }

        // Enable layer visibility and give the feature the sublayer's popup template
        // (required for programmatic popup.features = [feature] to show content).
        const parentLayer = _findMapImageLayerByBaseUrl(currentView.map, baseUrl);
        if (parentLayer) {
            _enableSublayerWithParents(parentLayer, layerId);
            setLayersStructure(prev => [...prev]);
            try {
                const sublayer = parentLayer.findSublayerById(parseInt(layerId, 10));
                if (sublayer?.popupTemplate) {
                    feature.popupTemplate = sublayer.popupTemplate.clone();
                }
            } catch (_) {}
        }

        // Zoom to feature
        const goTarget = (geom?.type === "polygon" || geom?.type === "polyline")
            ? { target: geom.extent?.expand(1.5) }
            : { target: geom, zoom: 18 };
        await currentView.goTo(goTarget, { animate: true, duration: 800 }).catch(() => {});

        // Wait for the view to finish navigating before touching popup state.
        await new Promise(r => setTimeout(r, 250));

        // Location fallback: point geom > polygon/polyline extent center > current view center
        const popupLocation =
            geom?.type === "point"
                ? geom
                : (geom?.extent?.center ?? currentView.center);

        // SDK 5.x: popup.open() exists but doesn't reliably show programmatic features.
        // Property assignment is the consistent path that works.
        // Flag as programmatic so the info-mode watcher doesn't close it.
        try {
            programmaticRef.current = true;
            currentView.popup.location = popupLocation;
            currentView.popup.features = [feature];
            currentView.popup.visible = true;
            setTimeout(() => { programmaticRef.current = false; }, 200);
        } catch (e) {
            programmaticRef.current = false;
            console.warn("Popup open error:", e);
        }
    }, []);

    const performSearch = useCallback(async (term, excludeKadastri = false) => {
        if (!term || term.length < 2) {
            setSearchResults([]);
            return;
        }
        const sources = searchSourcesRef.current;
        if (!sources.length) return;

        const activeSources = excludeKadastri
            ? sources.filter(s => s.name !== "საკადასტრო მონაცემები")
            : sources;

        const escapedTerm = term.replace(/'/g, "''");
        const allResults = [];
        await Promise.all(activeSources.map(async (source) => {
            const { layer, searchFields, displayField, name, customData } = source;
            const whereClause = searchFields
                .map(f => `UPPER(${f}) LIKE UPPER('%${escapedTerm}%')`)
                .join(" OR ");
            try {
                const result = await layer.queryFeatures({
                    where: whereClause,
                    outFields: ["*"],
                    returnGeometry: true,
                    num: 10,
                });
                if (result.features?.length) {
                    allResults.push({ sourceName: name, customData, displayField, features: result.features, layer });
                }
            } catch (e) {
                console.warn(`Search error in ${name}:`, e);
            }
        }));
        setSearchResults(allResults);
    }, []);

    useEffect(() => {
        if (!mapDivRef.current) return;

        const token = localStorage.getItem("jwtToken");
        if (token && role === null) return;

        esriConfig.request.corsDetection = false;
        esriConfig.request.proxyUrl = `${cleanBaseURL}/DotNet/proxy.ashx`;

        urlUtils.addProxyRule({
            urlPrefix: "https://gis.economy.ge",
            proxyUrl: `${cleanBaseURL}/DotNet/proxy.ashx`,
        });

        // The proxy.config on the TCSA server does not whitelist the PrintingTools
        // GPServer endpoint, so all print requests return 403 through the proxy.
        // Bypass the proxy for that specific URL so the browser calls it directly.
        // Requires CORS to be enabled on gis.economy.ge for this endpoint.
        esriConfig.request.interceptors.push({
            urls: "https://gis.economy.ge/server/rest/services/Utilities/PrintingTools",
            before(params) {
                params.requestOptions = params.requestOptions ?? {};
                params.requestOptions.useProxy = false;
            },
        });

        let view;
        let measureObs;
        let popupObs;
        let zoomClickHandler;

        const GEO_MEASURE = {
            "New measurement": "ახალი გაზომვა",
            "Clear": "გასუფთავება",
            "Distance": "მანძილი",
            "Area": "ფართობი",
            "Meters": "მეტრი",
            "Kilometers": "კილომეტრი",
            "Feet": "ფუტი",
            "Miles": "მილი",
            "Hectares": "ჰექტარი",
            "Acres": "აკრი",
            "Square meters": "კვ.მ",
            "Square kilometers": "კვ.კმ",
            "Square feet": "კვ.ფუტი",
            "Square miles": "კვ.მილი",
            "Measurement result": "შედეგი",
            "Unit": "ერთეული",
        };
        const translateMeasure = (root) => {
            if (!root) return;
            const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
            const nodes = [];
            let n;
            while ((n = walker.nextNode())) {
                const txt = n.nodeValue?.trim();
                if (txt && GEO_MEASURE[txt]) nodes.push([n, GEO_MEASURE[txt]]);
            }
            nodes.forEach(([n, geo]) => { n.nodeValue = geo; });
        };

        const initMap = async () => {
            let configData = [];
            try {
                const response = await esriRequest("https://gis.economy.ge/server/rest/services/TCSA/ManageMapLayersConfig/MapServer/0/query", {
                    query: { f: "json", where: "1=1", outFields: "*" },
                    responseType: "json"
                });
                configData = response.data.features.map(f => f.attributes);
            } catch (e) {
                console.error("Config failed:", e);
            }

            const targetUrl = "https://gis.economy.ge/server/rest/services/TCSA/TCSAGraphicsOld/MapServer";

            const patchedConfigData = configData
                .filter(item => {
                    if (item.URL?.toLowerCase().includes("test")) {
                        return role !== null && role !== "USER";
                    }
                    return true;
                })
                .map(item => {
                    if (item.URL?.toLowerCase().includes("test")) {
                        return { ...item, URL: targetUrl };
                    }
                    return item;
                });

            const dynamicLayers = createDynamicLayers(patchedConfigData, MapImageLayer);

            const sortedDynamicLayers = [...dynamicLayers].sort((a, b) => {
                const sortA = patchedConfigData.find(c => a.url.includes(c.URL))?.sort || 999;
                const sortB = patchedConfigData.find(c => b.url.includes(c.URL))?.sort || 999;
                return sortA - sortB;
            });

            const map = new Map({
                basemap: "hybrid",
                layers: [...sortedDynamicLayers].reverse()
            });

            view = new MapView({
                container: mapDivRef.current,
                map: map,
                center: [43.367997, 42.095516],
                zoom: 8,
                popup: { defaultPopupTemplateEnabled: true },
                theme: "light",
            });
            viewRef.current = view;

            // Push all built-in UI widgets below the fixed header and right of the action bar.
            // Left padding is updated dynamically when the panel opens/closes (see InteractiveMap.js).
            const isMobile = window.innerWidth < 768;
            // On mobile the map container itself is shifted 50px right (see .im-map CSS),
            // so the action bar is outside the container — no extra left padding needed.
            view.ui.padding = { top: isMobile ? 15 : 65, left: 55, right: 15, bottom: 30 };

            // On mobile, force the popup into docked-at-bottom mode from the first open.
            // Without this the popup opens as a floating balloon (photo-1 state) and the
            // user has to click the expand button to get the desired bottom-sheet layout.
            if (isMobile) {
                try {
                    view.popup.dockEnabled = true;
                    view.popup.dockOptions = {
                        position: "bottom-center",
                        breakpoint: { width: 768 },
                        buttonEnabled: false,
                    };
                } catch (_) {}
            }

            // Remove SDK's built-in zoom/home/locate buttons — replaced by custom
            // React buttons in InteractiveMap.js so we can fully control their style.
            view.ui.remove("zoom");
            homeRef.current = new Home({ view });
            locateRef.current = new Locate({ view });

            view.when().then(() => {
                // DOM-level guard: hides the "დეტალური" row directly in the rendered popup
                // HTML, catching cases where async template stripping runs too late or
                // defaultPopupTemplateEnabled regenerates the template after our code runs.
                const HIDDEN_POPUP_LABELS = new Set(["დეტალური", "ბმული"]);
                const hideDetailField = () => {
                    const mapEl = mapDivRef.current;
                    if (!mapEl) return;
                    mapEl.querySelectorAll('.esri-feature-fields__field-header').forEach(th => {
                        if (HIDDEN_POPUP_LABELS.has(th.textContent?.trim())) {
                            const row = th.closest('tr');
                            if (row) row.style.display = 'none';
                        }
                    });
                };
                popupObs = new MutationObserver(hideDetailField);
                if (mapDivRef.current) {
                    popupObs.observe(mapDivRef.current, { childList: true, subtree: true });
                }

                // Enforce info mode: if popup opens from a map click while info mode
                // is OFF (infoModeRef.current === false), close it immediately.
                // Programmatic opens (from navigateToSearchResult) are exempted via programmaticRef.
                // We clear popup.features before closing so selectedFeature resets to null —
                // this ensures the selectedFeature watcher fires fresh on the next open and
                // re-applies the correct template (avoids stale/broken design after re-enable).
                reactiveUtils.watch(
                    () => view.popup.visible,
                    (visible) => {
                        if (!visible) { view.graphics.removeAll(); return; }
                        if (!infoModeRef.current && !programmaticRef.current) {
                            view.popup.features = [];
                            view.popup.visible = false;
                        }
                    }
                );

                // Zoom to point features when the popup opens via a direct map click.
                // Template modification is intentionally absent here — the auto-generated
                // template (defaultPopupTemplateEnabled) already looks correct, and modifying
                // it asynchronously caused inconsistent field sets after info-mode toggling.
                // "დეტალური" / "ბმული" rows are hidden by the MutationObserver above.
                reactiveUtils.watch(
                    () => view.popup.selectedFeature,
                    (feature) => {
                        if (!feature || !view.popup.visible) return;
                        const geom = feature.geometry;
                        if (geom?.type !== "point") return;
                        if (view.zoom >= 14) return;
                        view.goTo({ target: geom, zoom: 18 }, { animate: true, duration: 1000 }).catch(() => {});
                    }
                );

                // Intercept the "Zoom to" popup button via document capture.
                // SDK 5.x Calcite popup renders actions in shadow DOM so trigger-action
                // is unreliable; composedPath() exposes shadow DOM elements on every click.
                const _doPopupZoom = () => {
                    const feature = view.popup.selectedFeature;
                    const geom = feature?.geometry;
                    const loc = view.popup.location;
                    if (geom?.type === "polygon" || geom?.type === "polyline") {
                        view.goTo({ target: geom.extent?.expand(1.5) }, { animate: true }).catch(() => {});
                    } else {
                        const pt = (geom?.type === "point" ? geom : null) ?? loc;
                        if (pt) view.goTo({ target: pt, zoom: 18 }, { animate: true, duration: 1000 }).catch(() => {});
                    }
                };

                zoomClickHandler = (e) => {
                    if (!view?.popup?.visible) return;
                    const path = e.composedPath ? e.composedPath() : [];
                    // Calcite popup elements (calcite-panel, calcite-flow-item, etc.) don't
                    // have "popup" in their tag/class, so we drop the inPopup guard and rely
                    // on popup.visible being true to avoid false positives.
                    const isZoomBtn = path.some(el => {
                        if (!el || typeof el.getAttribute !== "function") return false;
                        const tag = (el.tagName ?? "").toUpperCase();
                        if (tag !== "CALCITE-ACTION" && tag !== "BUTTON") return false;
                        const label = (
                            el.getAttribute("text") ??
                            el.getAttribute("title") ??
                            el.getAttribute("aria-label") ??
                            el.textContent ??
                            ""
                        ).toLowerCase();
                        return label.includes("zoom");
                    });
                    // Delay so our zoom runs after the SDK's built-in "Zoom to" handler.
                    // The SDK's default only centers point features without changing zoom level;
                    // our _doPopupZoom then overrides with the correct zoom-to-18 behavior.
                    if (isZoomBtn) setTimeout(_doPopupZoom, 50);
                };
                document.addEventListener("click", zoomClickHandler, true);

                // trigger-action as secondary fallback (fires when SDK does dispatch it)
                try {
                    view.popup.on("trigger-action", (event) => {
                        const id = (event.action?.id ?? "").toLowerCase();
                        if (id.includes("zoom")) setTimeout(_doPopupZoom, 50);
                    });
                } catch (e) {}

                try {
                    const measurement = new Measurement({ view });
                    view.ui.add(measurement, "bottom-right");
                    measurementRef.current = measurement;

                    // Translate measurement widget text to Georgian
                    measureObs = new MutationObserver(() => {
                        const el = mapDivRef.current?.querySelector(
                            ".esri-distance-measurement-2d, .esri-area-measurement-2d"
                        );
                        if (el) translateMeasure(el);
                    });
                    if (mapDivRef.current) {
                        measureObs.observe(mapDivRef.current, { childList: true, subtree: true });
                    }
                } catch (e) {
                    console.warn("Measurement widget error:", e);
                }

                try {
                    const printWidget = new Print({
                        view,
                        printServiceUrl: "https://gis.economy.ge/server/rest/services/Utilities/PrintingTools/GPServer/Export%20Web%20Map%20Task",
                    });
                    const printExpanddiv = document.getElementById("printExpandDiv");
                    if (printExpanddiv) {
                        printWidget.container = printExpanddiv;
                    }
                } catch (e) {
                    console.warn("Print widget error:", e);
                }

                try {
                    const basemapContainer = document.getElementById("basemapGalleryDiv");
                    setupBasemapGallery(view, BasemapGallery, reactiveUtils, t, {
                        onBasemapChange: (isDark) => setIsLightLayer(!isDark),
                        panelContainer: basemapContainer,
                    });
                } catch (e) {
                    console.warn("BasemapGallery error:", e);
                }
            }).catch(e => console.warn("view.when() error:", e));

            // Store search sources for custom search — no ArcGIS Search widget
            const searchSources = createSearchSources(configData, FeatureLayer);
            searchSourcesRef.current = searchSources;

            // Legend & Sidebar Structure
            const loadPromises = sortedDynamicLayers.map(l => l.load().catch(() => null));
            await Promise.all(loadPromises);

            // Re-run configurePopups here (after all layers are guaranteed loaded) to fix
            // the race condition where layer.when() inside createDynamicLayers could fire
            // after a user's first click, causing the auto-generated template to show instead.
            for (const layer of sortedDynamicLayers) {
                if (layer.sublayers) {
                    configurePopups(layer.sublayers, patchedConfigData);
                }
            }

            const finalSidebarStructure = [];
            const processSublayers = (sublayers, legendData, parentLayerTitle) => {
                sublayers.items.forEach(sub => {
                    sub.uniqueKey = `${parentLayerTitle}_${sub.id}`;
                    const subLayerLegend = legendData.find(l => l.layerId === sub.id);
                    if (subLayerLegend?.legend?.length > 0) {
                        const icon = subLayerLegend.legend[0];
                        sub.legendImage = `data:${icon.contentType};base64,${icon.imageData}`;
                    }
                    if (sub.sublayers?.items.length > 0) {
                        processSublayers(sub.sublayers, legendData, parentLayerTitle);
                    }
                });
            };

            for (const layer of sortedDynamicLayers) {
                let legendData = [];
                try {
                    const legRes = await esriRequest(`${layer.url}/legend`, {
                        query: { f: "json" },
                        responseType: "json"
                    });
                    legendData = legRes.data.layers;
                } catch (e) { console.warn("Legend error", e); }

                if (layer.sublayers) {
                    processSublayers(layer.sublayers, legendData, layer.title);
                    const serviceSublayers = layer.sublayers.items.map(item => item).reverse();
                    serviceSublayers.forEach(item => finalSidebarStructure.push(item));
                }
            }
            setLayersStructure(finalSidebarStructure);
        };

        initMap();

        return () => {
            measureObs?.disconnect();
            popupObs?.disconnect();
            if (zoomClickHandler) document.removeEventListener("click", zoomClickHandler, true);
            if (view) {
                view.destroy();
                viewRef.current = null;
            }
        };
    }, [mapDivRef, role, t]);

    const setInfoMode = useCallback((active) => {
        infoModeRef.current = active;
    }, []);

    return {
        layersStructure,
        setLayersStructure,
        viewRef,
        isLightLayer,
        measurementRef,
        homeRef,
        locateRef,
        searchResults,
        performSearch,
        navigateToSearchResult,
        setInfoMode,
    };
};
